import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
  Box,
  Divider,
  Avatar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CloudUpload,
  Image,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { categoryService } from '../services/api';
import { ModalGridContainer, ModalFormField } from './common/ModalGrid';
import CategoryPdfsManager from './CategoryPdfsManager';

const CategoryForm = ({ open, onClose, onSubmit, category = null, loading = false }) => {
  const { t, i18n } = useTranslation();
  const isEdit = Boolean(category);

  const [formData, setFormData] = useState({
    nameEn: '',
    nameGr: '',
    iconUrl: '',
    order: 0,
    isActive: true,
    type: 'video',
    pdfs: []
  });

  const [errors, setErrors] = useState({});
  const [uploadLoading, setUploadLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (open) {
      if (category) {
        setFormData({
          nameEn: category.nameEn || '',
          nameGr: category.nameGr || '',
          iconUrl: category.iconUrl || '',
          order: category.order || 0,
          isActive: category.isActive !== undefined ? category.isActive : true,
          type: category.type === 'pdf' ? 'pdf' : 'video',
          pdfs: category.pdfs || []
        });

        // Set preview URL for existing icon
        if (category.iconFullUrl) {
          setPreviewUrl(category.iconFullUrl);
        } else if (category.iconUrl) {
          const CDN_URL = process.env.REACT_APP_CDN_URL || 'https://cdn.worshipcoffee.eu';
          const fullIconUrl = category.iconUrl.startsWith('http')
            ? category.iconUrl
            : `${CDN_URL}/${category.iconUrl}`;
          setPreviewUrl(fullIconUrl);
        }
      } else {
        setFormData({
          nameEn: '',
          nameGr: '',
          iconUrl: '',
          order: 0,
          isActive: true,
          type: 'video',
          pdfs: []
        });
        setPreviewUrl(null);
      }
      setSelectedFile(null);
      setErrors({});
    }
  }, [category, open]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors({ iconUrl: t('categories.invalidFileType') });
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setErrors({ iconUrl: t('categories.fileTooLarge') });
      return;
    }

    setSelectedFile(file);
    setErrors({ ...errors, iconUrl: '' });

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveIcon = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData(prev => ({ ...prev, iconUrl: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nameEn.trim()) {
      newErrors.nameEn = t('categories.categoryNameRequired');
    }
    if (!formData.nameGr.trim()) {
      newErrors.nameGr = t('categories.categoryNameRequired');
    }

    // Check if icon is required (new category or removed icon)
    if (!isEdit && !selectedFile && !formData.iconUrl) {
      newErrors.iconUrl = t('categories.categoryIconRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setUploadLoading(true);
    try {
      let finalIconUrl = formData.iconUrl;

      // Upload new icon if selected
      if (selectedFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        uploadFormData.append('folder', 'categories');

        try {
          const uploadResponse = await categoryService.uploadIcon(uploadFormData);
          finalIconUrl = uploadResponse.filePath || uploadResponse.path || uploadResponse.url;
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          setErrors({ iconUrl: t('categories.failedToUploadIcon') });
          setUploadLoading(false);
          return;
        }
      }

      // Submit form data
      const submitData = {
        nameEn: formData.nameEn.trim(),
        nameGr: formData.nameGr.trim(),
        iconUrl: finalIconUrl,
        order: parseInt(formData.order) || 0,
        isActive: formData.isActive,
        type: formData.type
      };

      // If editing, include the icon field for backward compatibility
      if (isEdit) {
        submitData.icon = '';
        // Persist PDF rename/reorder (add/remove happen immediately via the manager).
        if (formData.type === 'pdf') {
          submitData.pdfs = formData.pdfs.map(p => ({ _id: p._id, name: p.name, order: p.order }));
        }
      }

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ general: t('common.error') });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !uploadLoading) {
      onClose();
    }
  };

  // Get display name based on current language for the dialog title
  const getDialogTitle = () => {
    if (isEdit && category) {
      const categoryName = i18n.language === 'gr' ? category.nameGr : category.nameEn;
      return `${t('categories.edit')}: ${categoryName}`;
    }
    return t('categories.add');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit
      }}
    >
      <DialogTitle>
        {getDialogTitle()}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Basic Information */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('customers.personalInfo')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <ModalGridContainer spacing={2}>
            <ModalFormField>
              <TextField
                name="nameGr"
                label={`${t('categories.nameGr')} *`}
                value={formData.nameGr}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                required
                error={!!errors.nameGr}
                helperText={errors.nameGr}
                disabled={loading || uploadLoading}
              />
            </ModalFormField>

            <ModalFormField>
              <TextField
                name="nameEn"
                label={`${t('categories.nameEn')} *`}
                value={formData.nameEn}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                required
                error={!!errors.nameEn}
                helperText={errors.nameEn}
                disabled={loading || uploadLoading}
              />
            </ModalFormField>

            <ModalFormField>
              <FormControl fullWidth variant="outlined" disabled={loading || uploadLoading}>
                <InputLabel id="category-type-label">{t('categories.type')}</InputLabel>
                <Select
                  labelId="category-type-label"
                  name="type"
                  value={formData.type}
                  label={t('categories.type')}
                  onChange={handleChange}
                >
                  <MenuItem value="video">{t('categories.typeVideo')}</MenuItem>
                  <MenuItem value="pdf">{t('categories.typePdf')}</MenuItem>
                </Select>
              </FormControl>
            </ModalFormField>

            <ModalFormField>
              <TextField
                name="order"
                label={t('categories.order')}
                type="number"
                value={formData.order}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                inputProps={{ min: 0 }}
                disabled={loading || uploadLoading}
              />
            </ModalFormField>

            <ModalFormField>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleChange}
                    name="isActive"
                    disabled={loading || uploadLoading}
                  />
                }
                label={t('categories.isActive')}
              />
            </ModalFormField>
        </ModalGridContainer>

        {/* Icon Upload Section */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('categories.categoryIcon')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            {/* Icon Preview */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('categories.iconPreview')}
              </Typography>
              <Avatar
                src={previewUrl}
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'grey.200',
                  border: '2px solid',
                  borderColor: 'divider'
                }}
                variant="rounded"
              >
                {!previewUrl && <Image sx={{ fontSize: 40, color: 'grey.400' }} />}
              </Avatar>
            </Box>

            {/* Upload Controls */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('categories.uploadNewIcon')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUpload />}
                  disabled={loading || uploadLoading}
                  size="small"
                >
                  {t('categories.selectImage')}
                  <input
                    type="file"
                    hidden
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleFileSelect}
                  />
                </Button>

                {previewUrl && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveIcon}
                    disabled={loading || uploadLoading}
                    size="small"
                  >
                    {t('categories.removeIcon')}
                  </Button>
                )}
              </Box>

              <Typography variant="caption" color="text.secondary">
                {t('categories.imageRequirements')}
              </Typography>

              {errors.iconUrl && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors.iconUrl}
                </Alert>
              )}

              {selectedFile && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('categories.selectedFile')}: {selectedFile.name}
                </Alert>
              )}
            </Box>
          </Box>

        {/* PDF Documents Section (only for PDF categories) */}
        {formData.type === 'pdf' && (
          <>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 3 }}>
              {t('categories.pdfs')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <CategoryPdfsManager
              categoryId={category?._id || category?.id}
              pdfs={formData.pdfs}
              onChange={(next) => setFormData(prev => ({ ...prev, pdfs: next }))}
            />
          </>
        )}

        {errors.general && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.general}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'flex-end' }}>
        <Button
          onClick={handleClose}
          disabled={loading || uploadLoading}
          size="large"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || uploadLoading}
          sx={{ minWidth: 120 }}
        >
          {loading || uploadLoading ? t('common.saving') : (isEdit ? t('common.update') : t('common.create'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryForm;